import React, {
    Dispatch,
    FC,
    MutableRefObject,
    Ref,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import InfiniteScroller from '../InfiniteScroller';
import { getCalenderTableColumns, getKpiTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import Legend from '../Legend';
import { colorConstants } from 'config/colorConstants';
import CalenderViewPopup from './Modals/CalenderViewPopup';
import {
    calculateFontSize,
    compareUsers,
    extractErrorMessage,
    formatNumberWithCommas,
    generateErrorMsg,
    getConfig,
    sendNotification,
    transformFiltersV2,
} from 'utils/helperMethods';
import { EditOutlined, PlusSquareOutlined, DeleteOutlined } from '@ant-design/icons';
import { TPODataContext } from 'contexts/TPOContext';
import PlanSummaryContainer from './PlanSummary/PlanSummaryContainer';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import ErrorBox from '../ErrorBox/ErrorBox';
import RadioMenuPopup from 'components/molecules/common/modals/RadioMenuPopup';
import dayjs, { Dayjs } from 'dayjs';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useFetchKpiTableData } from 'hooks/tradePromoOptimization/events/usefetchKpiTableData';
import { useFetchCalenderData } from 'hooks/tradePromoOptimization/events/useFetchCalenderData';
import { queryClient } from 'services/reactQueryService';
import Footer from '../PricingSimulator/Footer';
import { UserContext } from 'contexts/ConfigContext';
import { mainSectionId } from 'components/organism/TradePromoOptimization/UpdateOrViewTPO';
import { errorStatuses, pollingStatuses } from 'components/organism/PricingSimulator/UpdateOrViewPricingSimulator';
import ConfirmationModal from '../common/modals/ConfirmationModal';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { ErrorMessages, PromotionTypes, tpoNotificationMessages } from 'utils/Enums';
import { Spin, Table } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { antIcon } from '../Filter/FilterApplyButton';
import TableErrorBox from '../ErrorBox/TableErrorBox';
import DeleteConfirmationModal from '../PricingSimulator/Modals/DeleteConfirmationModal';
import _ from 'lodash';
import DatePickerWithEpoch from 'components/atoms/EpochDatePicker';
import { PromoInfoFormFieldNames } from 'constants/tpo';
import { getEnabledDatesForCopyEvent, onChangeDateForCopyEvent } from './tpoHelperMethods';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export const promoStartKey = 'promo_start_timestamp';

export const CalenderTable = styled(Table)`
    // .ant-table-sticky-scroll {
    //     position: static;
    //     display: none;
    // }
    .ant-table-sticky-holder {
        z-index: 7;
    }
    .ant-table-sticky-scroll {
        z-index: 5;
    }
    .ant-table-cell-row-hover {
        background: unset !important;
    }
    .ant-table-cell-fix-left,
    .ant-table-cell-fix-right {
        background: inherit !important;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey} !important;
        padding: 0px;
        max-height: 44px;
        border-right: none !important;
    }
    // .ant-table-header col:not(:last-child),
    // .ant-table-body col:not(:last-child) {
    //     min-width: 100px;
    // }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border-radius: 4px;
    ${tw`border border-borderLinePurpuleAlt`}
`;

const calenderTableId = 'events-calender-table';
const kpiTableId = 'events-calender-table';
const nonCalenderColumnWidth = calculateFontSize(window.innerWidth, 160);
const firstLastColumnWidth = calculateFontSize(window.innerWidth, 50);

type Props = {
    kpiVariable: any;
    setKpiVariable: Function;
    mainSectionHeight: MutableRefObject<number | null>;
    triggerApiCall: number;
    setTriggerApiCall: Dispatch<SetStateAction<number>>;
    setOpenEventModal: Dispatch<
        SetStateAction<{
            eventId: null | string;
            open: boolean;
        }>
    >;
    setOpenPromoModal: Dispatch<
        SetStateAction<{
            eventId: null | string;
            eventPromoId: null | string;
            open: boolean;
        }>
    >;
    calenderDataObject: {
        calenderDataLoading: boolean;
        calenderDataError: any;
        calenderData: any;
        calenderDataCount: number;
        rawCount: number;
        calenderDataDeletedCount: number;
    };
    calenderVariable: any;
    setCalenderVariable: Function;
    calenderSearchKey: string;
    kpiSearchKey: string;
    openKpiViewPopup: boolean;
    setOpenKpiViewPopup: Function;
    applyFilterObject: any;
    isPublishing: null | string;
    availableFilters: any;
    selectedCalendarColumns?: string[];
};

const EventsTableContainer: FC<Props> = ({
    setOpenEventModal,
    setOpenPromoModal,
    mainSectionHeight,
    triggerApiCall,
    setTriggerApiCall,
    kpiVariable,
    setKpiVariable,
    calenderDataObject,
    calenderVariable,
    setCalenderVariable,
    calenderSearchKey,
    kpiSearchKey,
    openKpiViewPopup,
    setOpenKpiViewPopup,
    applyFilterObject,
    isPublishing,
    availableFilters,
    selectedCalendarColumns = [],
}) => {
    const { appConfig } = useContext(UserContext);
    const tradePromoOptimizationConfig = getConfig('tradePromoOptimization', appConfig);
    const { calendarFilterKeys } = tradePromoOptimizationConfig;
    const enableEditMode = tradePromoOptimizationConfig?.enableEditMode;
    const kpiViewOptions = tradePromoOptimizationConfig?.kpiViewOptions;
    const [openCalenderViewPopup, setOpenCalenderViewPopup] = useState(false);
    const [selectedKpiView, setSelectedKpiView] = useState<any>(kpiViewOptions[0]);

    //slider value
    const [calenderViewSliderValue, setCalenderViewSliderValue] = useState(26);

    //context tpo
    const {
        showCalenderChanges,
        optimizedPlanId,
        eventViewType,
        isKpiAbsView,
        selectedPlanInfo,
        planIdFromUrl,
        planMetaDataLoading,
        isTpmTab,
        showOptimizedPlanCalender,
        promoConfig,
        promoConfigLoading,
        dimensionConfigData,
        planEditMetaData,
        viewMode,
        selectedEventInfo,
        setSelectedEventInfo,
        promoConfigDictionary,
        markTpoPlanContentMutated,
    } = useContext(TPODataContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const limit = tradePromoOptimizationConfig?.tableLimit;
    const statuses = tradePromoOptimizationConfig?.statuses;
    const isCalenderView = eventViewType === 'calender';
    const [openDeleteEventModal, setOpenDeleteEventModal] = useState({ eventId: '', eventName: '', open: false });
    const [openConfirmationModal, setOpenConfirmationModal] = useState<{
        from: string;
        eventDetails: Record<string, any>;
        open: boolean;
    }>({ eventDetails: {}, from: '', open: false });
    const [openCopyEventConfirmationModal, setOpenCopyEventConfirmationModal] = useState<{
        eventId: string;
        eventName: string;
        promoName: string;
        promotionTypeId?: number;
        eventPromoId?: string;
        open: boolean;
    }>({ eventId: '', eventName: '', promoName: '', open: false });

    // State for EDLP date fields
    const [copyEventDates, setCopyEventDates] = useState<{
        promoStartDate: number | null;
        promoEndDate: number | null;
        craStartDate: number | null;
        craEndDate: number | null;
    }>({
        promoStartDate: null,
        promoEndDate: null,
        craStartDate: null,
        craEndDate: null,
    });
    const [openEventsOptionsPopup, setOpenEventsOptionsPopup] = useState({ open: false, eventKey: null });

    // Helper function to render date picker
    const renderDatePicker = (fieldName: string, label: string, value: number | null, copyValueTo?: string) => {
        const enabledDates = getEnabledDatesForCopyEvent(fieldName, copyEventDates, selectedPlanInfo?.planYear);
        // Open the picker on the earliest selectable date when it is in the future (e.g. plan year > current year, or a
        // linked start date sits in the future); otherwise let the picker default to today.
        const today = dayjs().startOf('day').unix();
        const defaultOpenDate = enabledDates.start && enabledDates.start > today ? enabledDates.start : null;
        return (
            <div className="flex-1">
                <label className="block text-sm font-medium mb-2">
                    <span className="text-redTriangle mr-1">*</span>
                    {label}
                </label>
                <DatePickerWithEpoch
                    value={value}
                    onChange={(date: number | null) => {
                        onChangeDateForCopyEvent(
                            date,
                            fieldName,
                            copyEventDates,
                            (updates) => setCopyEventDates({ ...copyEventDates, ...updates }),
                            copyValueTo,
                        );
                    }}
                    format="MM/DD/YYYY"
                    placeholder="Select date"
                    showToday={true}
                    classNames="!w-full !rounded-md"
                    enabledDates={enabledDates}
                    defaultOpenDate={defaultOpenDate}
                />
            </div>
        );
    };

    //kpi error loading count
    function infiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        const newOffset = kpiVariable.offset + kpiVariable.limit;
        setKpiVariable({ ...kpiVariable, offset: newOffset });
    }

    const tableHeight = useRef<number | null>(0);
    const calenderTableRef = useRef<HTMLDivElement | null>(null);

    const {
        calenderDataLoading,
        calenderDataError,
        calenderData,
        calenderDataCount,
        rawCount,
        calenderDataDeletedCount,
    } = calenderDataObject;
    const {
        calenderColumns,
        nonCalenderColumns,
        result: calenderParsedData,
        hoverData,
        legendObj,
    } = calenderData ?? {};

    //kpi table api
    const {
        loading: kpiTableDataLoading,
        error: kpiTableDataError,
        data: kpiTableData,
        count: kpiTableDataCount,
    } = useFetchKpiTableData({
        method: 'post',
        queryConfig: {
            enabled:
                !isCalenderView &&
                Boolean(kpiVariable?.group_by?.length > 0 && kpiVariable?.kpi_list && kpiVariable?.plan_id) &&
                kpiVariable.kpi_list.length > 0,
        },
        url: tradePromoOptimizationUrls.getKpiTableDataUrl,
        queryKey: ['tpo-event-data', 'tpo-kpi-table-data', kpiVariable],
        variables: { ...(kpiVariable || {}) },
        extras: { dimensionDropdownList: dimensionConfigData?.dimensionDropdownList, eventViewType },
    });

    const { mutate: deleteEvent, loading: deleteEventLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.deleteEventUrl}/${openDeleteEventModal?.eventId}`,
        method: 'delete',
    });

    const { mutate: keepDatesPromo, loading: keepDatesPromoLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getKeepDatesPromoUrl}`,
        method: 'post',
    });

    const { mutate: copyEvent, loading: copyEventLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.copyEventPromoUrl}`,
        method: 'post',
    });

    const { kpiColumns, nonKpiColumns, result: kpiData } = kpiTableData ?? {};
    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: calenderTableId,
        height: tableHeight.current,
        buffer: 140, //footer + legend
        //triggerDummyRender: true,
    });

    function onChangeCalenderViewClickHandler() {
        setOpenCalenderViewPopup(true);
    }

    //kpi table columns
    const memoisedKpiTableColumns = useMemo(() => {
        if (!isCalenderView && kpiData?.length > 0) {
            //generate dynamic table sub header keys based on kpi view selected
            // For plan_metrics view, use default kpi view (first option) since there's no "Change KPI View" option
            // Only promo_metrics view should use the selectedKpiView
            const isPlanMetricsView = eventViewType === 'plan_metrics';
            const kpiViewToUse = isPlanMetricsView ? kpiViewOptions[0] : selectedKpiView;
            const tableSubHeader = [...(kpiViewToUse?.tableSubHeader ?? [])];
            if (tableSubHeader?.length > 1) tableSubHeader?.splice(1, 0, isKpiAbsView ? 'Diff (Abs)' : 'Diff (%)');
            return getKpiTableColumns(
                kpiColumns,
                nonKpiColumns,
                310,
                firstLastColumnWidth,
                setOpenEventsOptionsPopup,
                openEventsOptionsPopup,
                calenderOptionsClickHandler,
                commonConfig,
                isKpiAbsView,
                tableSubHeader,
            );
        }
    }, [kpiData, isCalenderView, isKpiAbsView, eventViewType, kpiViewOptions, selectedKpiView]);

    //called when clicked on calender 3 dots
    function calenderOptionsClickHandler(eventId: string, action: string, eventDetails: any) {
        setOpenEventsOptionsPopup({ eventKey: null, open: false });
        switch (action) {
            case 'add_promo':
                // Set is_ever_successfully_published in selectedEventInfo when opening promo modal
                if (eventDetails?.is_ever_successfully_published !== undefined) {
                    setSelectedEventInfo({
                        ...(selectedEventInfo ?? {}),
                        is_ever_successfully_published: eventDetails.is_ever_successfully_published,
                    });
                }
                setOpenPromoModal({ eventId, open: true, eventPromoId: eventDetails.eventPromoId });
                return;
            case 'edit_event':
                setOpenEventModal({ eventId, open: true });
                return;
            case 'delete':
                setOpenDeleteEventModal({ eventId, eventName: eventDetails?.event, open: true });
                return;
            case 'keep_dates':
                setOpenConfirmationModal({ eventDetails, open: true, from: 'keep_dates' });
                return;
            case 'keep_promo':
                setOpenConfirmationModal({ eventDetails, open: true, from: 'keep_promo' });
                return;
            case 'copy_event':
                setOpenCopyEventConfirmationModal({
                    eventId,
                    eventName: eventDetails?.event,
                    promoName: eventDetails?.promo,
                    promotionTypeId: eventDetails?.promotion_type_id,
                    eventPromoId: eventDetails?.eventPromoId,
                    open: true,
                });
                // Reset dates - no default selection
                setCopyEventDates({
                    promoStartDate: null,
                    promoEndDate: null,
                    craStartDate: null,
                    craEndDate: null,
                });
                return;
            default:
                return;
        }
    }

    function handleCopyPromoSubmit() {
        const isEdlp = Number(openCopyEventConfirmationModal?.promotionTypeId) === PromotionTypes.EDLP;

        const payload: any = {
            event_id: openCopyEventConfirmationModal.eventId,
            event_promo_id: openCopyEventConfirmationModal.eventPromoId,
            plan_id: selectedPlanInfo?.planId,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            created_by: selectedPlanInfo?.createdBy,
            is_edlp: isEdlp,
            promo_dates: {
                promo_start: copyEventDates.promoStartDate,
                promo_end: copyEventDates.promoEndDate,
                cra_start: copyEventDates.craStartDate,
                cra_end: copyEventDates.craEndDate,
            },
        };
        copyEvent(payload, {
            onSuccess: (data: any) => {
                sendNotification('success', data?.data?.message || tpoNotificationMessages.COPY_EVENT_SUCCESS);
                setTriggerApiCall(triggerApiCall + 1);
                setOpenCopyEventConfirmationModal({ eventId: '', eventName: '', promoName: '', open: false });
                // Reset dates after successful copy
                setCopyEventDates({
                    promoStartDate: null,
                    promoEndDate: null,
                    craStartDate: null,
                    craEndDate: null,
                });
                // // Open promo modal after successful copy if eventId and promoId are available in response
                const copiedEventId = data?.data?.event_id || data?.data?.eventId;
                const copiedPromoId = data?.data?.event_promo_id;
                if (copiedEventId && copiedPromoId) {
                    setOpenPromoModal({
                        eventId: String(copiedEventId),
                        open: true,
                        eventPromoId: String(copiedPromoId),
                    });
                }
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(extractErrorMessage(error), ErrorMessages.GENERALIZED_ERROR_MSG);
                sendNotification('error', errorMsg);
            },
        });
    }

    //calender table columns
    const memoisedCalenderTableColumns = useMemo(() => {
        if (isCalenderView) {
            // Filter nonCalenderColumns based on selected columns
            const filteredNonCalenderColumns =
                selectedCalendarColumns?.length > 0 && dimensionConfigData?.calendarColumnDropdownList
                    ? nonCalenderColumns?.filter((item: any) => {
                          const selectedColumnConfig = dimensionConfigData?.calendarColumnDropdownList?.find(
                              (configItem: any) =>
                                  selectedCalendarColumns.includes(configItem.value) &&
                                  configItem.dataKey === item?.key,
                          );
                          return !!selectedColumnConfig;
                      }) || []
                    : nonCalenderColumns?.filter((item: any) =>
                          ['event', 'promo', 'promoStatus', 'planId'].includes(item?.key),
                      ) || [];

            //calculate calender width depending on slider value
            //subtract 1st, last and non calender columns like events etc
            const calenderViewWidth =
                (((calenderTableRef?.current?.offsetWidth ?? window.innerWidth) -
                    (nonCalenderColumnWidth * (filteredNonCalenderColumns?.length ?? 0) + firstLastColumnWidth * 2)) /
                    calenderViewSliderValue) *
                4;
            const widthObj = {
                calenderWidth: calenderViewWidth,
                nonCalenderWidth: nonCalenderColumnWidth,
                firstLastColumnWidth,
            };
            const updatedNonCalenderColumns = filteredNonCalenderColumns?.map((item: any) => {
                return {
                    label: item?.key === 'planId' ? 'TPO ID' : item?.label,
                    key: item?.key,
                    sortKey: item?.sortKey,
                };
            });
            return getCalenderTableColumns(
                updatedNonCalenderColumns,
                calenderColumns,
                widthObj,
                hoverData,
                onChangeCalenderViewClickHandler,
                showCalenderChanges,
                calenderOptionsClickHandler,
                openEventsOptionsPopup,
                setOpenEventsOptionsPopup,
                isTpmTab,
                {
                    sortBy: calenderVariable?.sort_by || 0,
                    sortOrder:
                        calenderVariable?.sorting_order === 'asc'
                            ? 'ascend'
                            : calenderVariable?.sorting_order === 'desc'
                            ? 'descend'
                            : null,
                },
                viewMode,
                selectedPlanInfo,
                isPublishing,
                promoConfigDictionary,
            );
        }
    }, [
        showCalenderChanges,

        isCalenderView,

        calenderData,

        calenderViewSliderValue,

        openEventsOptionsPopup,

        viewMode,
        selectedCalendarColumns,
        nonCalenderColumns,
        ,
        isPublishing,
        promoConfigDictionary,
    ]);

    //calender legend
    const calenderLegend: any[] = useMemo(() => {
        return Object.values(legendObj ?? {}).map((val) => {
            return val;
        });
    }, [legendObj]);

    useEffect(() => {
        const updatedFilters = transformFiltersV2(_.cloneDeep(applyFilterObject), availableFilters);
        if (promoConfig?.length) {
            setCalenderVariable({
                search_string: calenderSearchKey ?? '',
                //plan_id: 18,
                plan_id: isTpmTab
                    ? selectedPlanInfo?.planId
                    : showOptimizedPlanCalender
                    ? optimizedPlanId || selectedPlanInfo?.planId
                    : selectedPlanInfo?.planId,
                is_tpm_plan: selectedPlanInfo?.isTpmPlan,
                is_tpm_tab: isTpmTab,
                sort_by: promoStartKey,
                limit: limit,
                offset: 0,
                sorting_order: 'asc',
                plan_year: selectedPlanInfo?.planYear,
                filters: updatedFilters,
            });
        }
        setKpiVariable((prev: any) => ({
            ...prev,
            search_key: kpiSearchKey ?? '',
            event_type: isTpmTab ? 'tpm' : 'tpo',
            plan_id: isTpmTab
                ? selectedPlanInfo?.planId
                : showOptimizedPlanCalender
                ? optimizedPlanId || selectedPlanInfo?.planId
                : selectedPlanInfo?.planId,
            is_tpm_plan: showOptimizedPlanCalender ? false : selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
            sort_by: '',
            limit: limit,
            offset: 0,
            sorting_order: 'asc',
            current_date: dayjs().unix(),
            filters: _.cloneDeep(updatedFilters),
        }));

        //remove queries from cache when planid changes to remove stale data
        queryClient.removeQueries({
            queryKey: ['tpo-event-data'],
            exact: false,
        });
        return () => {
            setCalenderVariable({});
            setKpiVariable((prev: any) => {
                const duplicatedKpiVariable = { ...(prev ?? {}) };
                delete duplicatedKpiVariable?.plan_id;
                return duplicatedKpiVariable;
            });
        };
    }, [
        selectedPlanInfo?.planId,
        promoConfig,
        showOptimizedPlanCalender,
        triggerApiCall,
        applyFilterObject,
        availableFilters,
    ]);

    //change the calender variables when search key changes
    useEffect(() => {
        setCalenderVariable({
            ...calenderVariable,
            search_string: calenderSearchKey ?? '',
            offset: 0,
        });
    }, [calenderSearchKey]);

    //change the kpi variables when search key changes
    useEffect(() => {
        setKpiVariable({
            ...kpiVariable,
            search_key: kpiSearchKey ?? '',
            offset: 0,
        });
    }, [kpiSearchKey]);

    //statuses
    const isErrorStatus = errorStatuses.includes(selectedPlanInfo?.status || '');
    const inProgressStatus = pollingStatuses.includes(selectedPlanInfo?.status || '');
    const isActive = selectedPlanInfo?.status === 'ACTIVE';

    const calenderFooterCount = Math.max(
        Number((calenderParsedData?.length ?? 0) - (showCalenderChanges ? 0 : calenderDataDeletedCount)),
        0,
    )?.toLocaleString();
    //footer text
    const footerFirstText = `${
        isCalenderView
            ? 'Showing ' + +calenderFooterCount + ' of ' + (formatNumberWithCommas(calenderDataCount, 0) + ' records')
            : 'Showing ' +
              Number(kpiData?.length ?? 0)?.toLocaleString() +
              ' of ' +
              formatNumberWithCommas(kpiTableDataCount, 0) +
              ' records'
    }`;
    //if a different user is editing plan , then show  the user's name
    const footerSecondText =
        enableEditMode && planEditMetaData?.editingUser && !Boolean(compareUsers(planEditMetaData?.editingUser))
            ? planEditMetaData?.editingUser + ' is currently editing'
            : '';
    const footerThirdText = selectedPlanInfo?.createdBy ? `Created by: ${selectedPlanInfo?.createdBy}` : '';
    const footerFourthText = selectedPlanInfo?.updatedAt ? `Last Updated: ${selectedPlanInfo?.updatedAt}` : '';
    const footerFifthText =
        calenderData?.statues?.publish_status === 'publish_in_progress' ? 'Plan Locked: Publish is in progress.' : '';

    function calenderInfiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        const newOffset = calenderVariable?.offset + calenderVariable?.limit;
        setCalenderVariable({ ...calenderVariable, offset: newOffset });
    }
    const commonCalenderLoading = promoConfigLoading || calenderDataLoading || planMetaDataLoading;
    const kpiCommonLoading = kpiTableDataLoading || planMetaDataLoading;

    const keepDatesPromoPopupSubTitle =
        openConfirmationModal?.from === 'keep_dates'
            ? openConfirmationModal?.eventDetails?.keepDates
                ? 'Are you sure you want to remove keep dates ?'
                : 'Are you sure you want to keep dates ?'
            : openConfirmationModal?.eventDetails?.keepPromo
            ? 'Are you sure you want to remove keep promo ?'
            : 'Are you sure you want to keep promo ?';
    const keepDatesPromoPopupTitle =
        openConfirmationModal?.from === 'keep_dates'
            ? openConfirmationModal?.eventDetails?.keepDates
                ? 'Remove Keep Dates'
                : 'Keep Dates'
            : openConfirmationModal?.eventDetails?.keepPromo
            ? 'Remove Keep Promo'
            : 'Keep Promo';
    const dataIncludingHeader = kpiData?.length ? [{ key: 'kpi-table-header' }, ...kpiData] : kpiData;
    return (
        <>
            <div
                className="bg-white p-4 border-x border-x-borderLinePurpuleAlt"
                id={mainSectionId}
                style={{ height: mainSectionHeight.current ? mainSectionHeight.current : 'auto' }}
            >
                {eventViewType === 'plan_summary' ? (
                    <PlanSummaryContainer
                        setKpiVariable={setKpiVariable}
                        kpiVariable={kpiVariable}
                        applyFilterObject={applyFilterObject}
                        availableFilters={availableFilters}
                    />
                ) : isCalenderView ? (
                    <>
                        <InfiniteScroller
                            style={{ overflowX: 'hidden' }}
                            data={calenderParsedData || []}
                            loading={commonCalenderLoading}
                            hasMore={calenderParsedData?.length < rawCount || commonCalenderLoading}
                            height={
                                calenderParsedData?.length && !calenderDataError
                                    ? tableHeight.current ?? undefined
                                    : isNaN(Number(tableHeight.current))
                                    ? undefined
                                    : Number(tableHeight.current) - 1
                            }
                            onNext={() => calenderInfiniteScrollOnNext(commonCalenderLoading)}
                        >
                            {/* TABLE */}
                            <CalenderTable
                                onChange={(pagination, filter, sort: any, extra) => {
                                    if (sort?.order) {
                                        const sortMapping: any = {
                                            descend: 'desc',
                                            ascend: 'asc',
                                        };

                                        setCalenderVariable({
                                            ...calenderVariable,
                                            sort_by: sort?.column?.sortKey,
                                            sorting_order: sortMapping?.[sort?.order] || 'asc',
                                            offset: 0,
                                        });
                                        //default it to dim1 column on cancelling sorting
                                    } else {
                                        setCalenderVariable({
                                            ...calenderVariable,
                                            sort_by: promoStartKey,
                                            sorting_order: 'asc',
                                            offset: 0,
                                        });
                                    }
                                }}
                                locale={{
                                    emptyText: commonCalenderLoading ? (
                                        <></>
                                    ) : calenderDataError ? (
                                        <TableErrorBox error={calenderDataError} height={tableHeight.current} />
                                    ) : (
                                        ''
                                    ),
                                }}
                                sticky={{
                                    getContainer: () =>
                                        document.getElementsByClassName(
                                            'infinite-scroll-component',
                                        )?.[0] as HTMLElement,
                                }} //adding x axis scrollbar on table
                                ref={calenderTableRef}
                                style={{
                                    opacity: commonCalenderLoading ? '0.3' : 1,
                                    pointerEvents: commonCalenderLoading ? 'none' : 'auto',
                                }}
                                bordered
                                pagination={false}
                                scroll={{ x: '100%' }}
                                id={calenderTableId}
                                columns={memoisedCalenderTableColumns}
                                dataSource={calenderParsedData || []}
                                // loading={commonCalenderLoading}
                                rowClassName={(rowItem: any, a, b) => {
                                    return `${
                                        showCalenderChanges && !isTpmTab
                                            ? rowItem?.operation?.backgroundColor
                                            : 'bg-white'
                                    }`;
                                }}
                            />
                        </InfiniteScroller>
                        {/* LEGEND */}
                        <Legend data={calenderLegend} />
                    </>
                ) : (
                    <InfiniteScroller
                        data={kpiData}
                        loading={kpiCommonLoading}
                        hasMore={kpiData?.length < (kpiTableDataCount || 0) || kpiCommonLoading}
                        height={
                            kpiData?.length && !kpiTableDataError
                                ? tableHeight.current ?? undefined
                                : isNaN(Number(tableHeight.current))
                                ? undefined
                                : Number(tableHeight.current) - 1
                        } //height={200}
                        //     loader={true}
                        onNext={() => infiniteScrollOnNext(kpiCommonLoading)}
                    >
                        <CustomTable
                            bordered
                            onChange={(pagination, filter, sort: any, extra) => {
                                if (sort?.order) {
                                    const sortMapping: any = {
                                        descend: 'desc',
                                        ascend: 'asc',
                                    };
                                    setKpiVariable({
                                        ...kpiVariable,
                                        sort_by: sort?.column?.sortKey,
                                        sorting_order: sortMapping?.[sort?.order] || 'asc',
                                        offset: 0,
                                    });
                                    //default it to dim1 column on cancelling sorting
                                }
                            }}
                            sticky={{
                                getContainer: () =>
                                    document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                            }} //adding x axis scrollbar on table
                            id={kpiTableId}
                            //loading={kpiCommonLoading}
                            columns={memoisedKpiTableColumns}
                            scroll={{ x: '100%' }}
                            dataSource={dataIncludingHeader}
                            pagination={false}
                            style={{
                                opacity: kpiCommonLoading ? '0.3' : 1,
                                pointerEvents: kpiCommonLoading ? 'none' : 'auto',
                            }}
                            locale={{
                                emptyText: kpiCommonLoading ? (
                                    <></>
                                ) : kpiTableDataError ? (
                                    <TableErrorBox error={kpiTableDataError} height={tableHeight.current} />
                                ) : (
                                    ''
                                ),
                            }}
                        />
                    </InfiniteScroller>
                )}

                <CalenderViewPopup
                    sliderValue={calenderViewSliderValue}
                    open={openCalenderViewPopup}
                    onCancel={() => {
                        setOpenCalenderViewPopup(false);
                    }}
                    onSubmit={(sliderVal, viewType) => {
                        setOpenCalenderViewPopup(false);
                        setCalenderViewSliderValue(sliderVal);
                    }}
                />
                <RadioMenuPopup
                    open={openKpiViewPopup}
                    onCancel={() => {
                        setOpenKpiViewPopup(false);
                    }}
                    onSubmit={(val) => {
                        setOpenKpiViewPopup(false);
                        setSelectedKpiView(val);
                        setKpiVariable({ ...kpiVariable, kpi_view: val?.key });
                    }}
                    value={selectedKpiView}
                    title="KPI View"
                    menuTitle="Choose view"
                    items={kpiViewOptions}
                />
            </div>
            {/* Plan summary has its own footer inside PlanSummaryContainer */}
            {eventViewType !== 'plan_summary' && (
                <Footer
                    type={isErrorStatus ? 'error' : inProgressStatus ? 'inProgress' : isActive ? 'success' : 'default'}
                    status={inProgressStatus ? null : statuses?.[selectedPlanInfo?.status || '']?.label}
                    items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText, footerFifthText]}
                />
            )}
            <ConfirmationModal
                open={openConfirmationModal?.open}
                onCancel={() => {
                    setOpenConfirmationModal({ eventDetails: {}, from: '', open: false });
                }}
                onSubmitClick={() => {
                    const payload = {
                        event_promo_id: openConfirmationModal.eventDetails?.eventPromoId,
                        keep_dates:
                            openConfirmationModal?.from === 'keep_dates'
                                ? !Boolean(openConfirmationModal?.eventDetails?.keepDates)
                                : Boolean(openConfirmationModal?.eventDetails?.keepDates),
                        keep_promo:
                            openConfirmationModal?.from === 'keep_promo'
                                ? !Boolean(openConfirmationModal?.eventDetails?.keepPromo)
                                : Boolean(openConfirmationModal?.eventDetails?.keepPromo),
                    };
                    keepDatesPromo(payload, {
                        onSuccess: (data: any) => {
                            sendNotification('success', tpoNotificationMessages.EDIT_KEEP_DATES_PROMO_SUCCESS);
                            setTriggerApiCall(triggerApiCall + 1);
                            setOpenConfirmationModal({ eventDetails: {}, from: '', open: false });
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail || ''),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                            // onCancel(val);
                        },
                    });
                }}
                okText={keepDatesPromoLoading ? <Spin indicator={antIcon} /> : 'Yes'}
                okButtonProps={{ type: 'primary', style: { borderRadius: 6 } }}
                loading={keepDatesPromoLoading}
                subTitle={keepDatesPromoPopupSubTitle}
                title={keepDatesPromoPopupTitle}
                description={``}
            />

            <DeleteConfirmationModal
                loading={deleteEventLoading}
                onSubmitClick={() => {
                    deleteEvent(openDeleteEventModal.eventId, {
                        onSuccess: (data: any) => {
                            if (data?.data?.success) {
                                markTpoPlanContentMutated();
                                sendNotification(
                                    'success',
                                    data?.data?.message || tpoNotificationMessages.DELETE_EVENT_SUCCESS,
                                );
                                setTriggerApiCall(triggerApiCall + 1);
                                setOpenDeleteEventModal({ eventId: '', eventName: '', open: false });
                            } else {
                                sendNotification(
                                    'error',
                                    data?.data?.message || tpoNotificationMessages.DELETE_EVENT_ERROR,
                                );
                            }
                        },
                    });
                }}
                onCancel={(e) => {
                    setOpenDeleteEventModal({ eventId: '', eventName: '', open: false });
                }}
                open={openDeleteEventModal.open}
                title={'Delete Event'}
                subTitle={`Are you sure you want to delete the event ${openDeleteEventModal.eventName}?`}
            />

            {/* COPY PROMO CONFIRMATION MODAL */}
            <ConfirmationModal
                open={openCopyEventConfirmationModal?.open}
                onCancel={() => {
                    setOpenCopyEventConfirmationModal({ eventId: '', eventName: '', promoName: '', open: false });
                    // Reset dates when modal closes
                    setCopyEventDates({
                        promoStartDate: null,
                        promoEndDate: null,
                        craStartDate: null,
                        craEndDate: null,
                    });
                }}
                onSubmitClick={handleCopyPromoSubmit}
                okText={copyEventLoading ? <Spin indicator={antIcon} /> : 'Yes'}
                okButtonProps={{ type: 'primary', style: { borderRadius: 6 } }}
                loading={copyEventLoading}
                subTitle={`Are you sure you want to copy this promo ${openCopyEventConfirmationModal.promoName}?`}
                title="Copy Promo"
                width={600}
                description={
                    <div className="mt-4">
                        <div className="flex gap-4">
                            {renderDatePicker(
                                PromoInfoFormFieldNames.PromoStartDate,
                                'Start Date',
                                copyEventDates.promoStartDate,
                                PromoInfoFormFieldNames.CRAStartDate,
                            )}
                            {renderDatePicker(
                                PromoInfoFormFieldNames.PromoEndDate,
                                'End Date',
                                copyEventDates.promoEndDate,
                                PromoInfoFormFieldNames.CRAEndDate,
                            )}
                        </div>
                    </div>
                }
            />
        </>
    );
};

export default EventsTableContainer;
